export type AutoStepKey =
  | 'detect'
  | 'preprocess'
  | 'similar'
  | 'text'
  | 'fingerprint'
  | 'fingerprintRecognition'
  | 'identify';

export type AutoDecisionAction =
  | 'run'
  | 'skipped'
  | 'stop'
  | 'failed'
  | 'manual_override';

export interface AutoDecision {
  id: string;
  step: AutoStepKey;
  action: AutoDecisionAction;
  reason: string;
  startedAt?: number;
  completedAt?: number;
  durationMs?: number;
  confidence?: number;
  detail?: string;
}

export interface AutoMatchConfig {
  acceptThreshold: number;
  candidateThreshold: number;
  minMargin: number;
  allowIdentifyShortcut: boolean;
  detectUploads: boolean;
  preprocessUploads: boolean;
  preprocessDetections: boolean;
}

export const AUTO_MATCH_CONFIG: AutoMatchConfig = {
  acceptThreshold: 0.92,
  candidateThreshold: 0.75,
  minMargin: 0.06,
  allowIdentifyShortcut: true,
  detectUploads: true,
  preprocessUploads: true,
  preprocessDetections: true,
};

export const AUTO_STEP_ORDER: AutoStepKey[] = [
  'detect',
  'preprocess',
  'similar',
  'text',
  'fingerprint',
  'fingerprintRecognition',
  'identify',
];

const cigarId = (candidate: any) =>
  candidate?.cigarId ?? candidate?.cigar_id ?? candidate?.id;

export const automaticCandidateScore = (candidate: any) => {
  const value =
    candidate?.confidence ?? candidate?.score ?? candidate?.similarity;
  const score = Number(value);
  if (!Number.isFinite(score)) return null;
  return score > 1 ? score / 100 : score;
};

export const candidatesForAutomaticStep = (
  step: AutoStepKey,
  result: any
): any[] => {
  if (!result) return [];
  if (step === 'text') {
    return [
      ...(result.proposedMatch ? [result.proposedMatch] : []),
      ...(result.textCandidates || result.text_candidates || []),
    ];
  }
  if (step === 'identify') {
    return [
      ...(result.match ? [result.match] : []),
      ...(result.suggestions || []),
      ...(result.consensus?.candidates || []),
      ...(result.research?.catalogCandidates || []),
    ];
  }
  return result.candidates || [];
};

export const automaticCandidateStrength = (
  candidates: any[],
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) => {
  const ranked = candidates
    .map((candidate) => ({
      candidate,
      score: automaticCandidateScore(candidate),
    }))
    .filter(
      (item): item is { candidate: any; score: number } => item.score !== null
    )
    .sort((left, right) => right.score - left.score);
  const top = ranked[0];
  const runnerUp = ranked[1];
  const margin = top ? top.score - (runnerUp?.score ?? 0) : 0;
  return {
    candidate: top?.candidate,
    confidence: top?.score ?? null,
    margin,
    sufficient: Boolean(
      top &&
        top.score >= config.acceptThreshold &&
        (!runnerUp || margin >= config.minMargin)
    ),
  };
};

export const shouldAcceptCandidate = (
  step: AutoStepKey,
  result: any,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) =>
  automaticCandidateStrength(candidatesForAutomaticStep(step, result), config);

export const shouldRunDetect = (
  sourceType: string,
  workingImageType: string,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) =>
  config.detectUploads &&
  sourceType === 'upload' &&
  workingImageType === 'original';

export const shouldRunPreprocess = (
  sourceType: string,
  workingImageType: string,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) =>
  (workingImageType === 'detected' && config.preprocessDetections) ||
  (sourceType === 'upload' &&
    workingImageType === 'original' &&
    config.preprocessUploads);

export const shouldRunSimilar = (preprocessStatus?: string) =>
  preprocessStatus !== 'skipped';

export const shouldRunText = (context: {
  detectResult?: any;
  preprocessResult?: any;
}) => {
  const cheapTextSignal =
    context.preprocessResult?.textLikely ??
    context.preprocessResult?.textVisible ??
    context.detectResult?.textLikely ??
    context.detectResult?.textVisible;
  return cheapTextSignal === false
    ? { run: false, reason: 'no_text_likely_visible' }
    : { run: true, reason: 'text_presence_unknown_conservative_fallback' };
};

const usefulTextEvidence = (result: any) =>
  Boolean(
    result?.rawText?.trim?.() ||
      result?.normalizedText?.trim?.() ||
      result?.tokens?.length ||
      candidatesForAutomaticStep('text', result).length
  );

const topCandidateId = (step: AutoStepKey, result: any) => {
  const { candidate } = automaticCandidateStrength(
    candidatesForAutomaticStep(step, result)
  );
  return candidate ? cigarId(candidate) : null;
};

export const hasMeaningfulCandidateConflict = (
  stages: Partial<Record<AutoStepKey, { result?: any }>>,
  currentStep: AutoStepKey,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) => {
  const currentCandidates = candidatesForAutomaticStep(
    currentStep,
    stages[currentStep]?.result
  );
  const current = automaticCandidateStrength(currentCandidates, config);
  const currentId = current.candidate ? cigarId(current.candidate) : null;
  if (!currentId || (current.confidence || 0) < config.candidateThreshold) {
    return false;
  }
  return (['similar', 'text', 'fingerprintRecognition'] as AutoStepKey[])
    .filter((step) => step !== currentStep && stages[step]?.result)
    .some((step) => {
      const prior = automaticCandidateStrength(
        candidatesForAutomaticStep(step, stages[step]?.result),
        config
      );
      const priorId = prior.candidate ? cigarId(prior.candidate) : null;
      return Boolean(
        priorId &&
          (prior.confidence || 0) >= config.candidateThreshold &&
          String(priorId) !== String(currentId)
      );
    });
};

export const shouldJumpToIdentify = (
  stages: Partial<Record<AutoStepKey, { result?: any }>>,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) => {
  if (!config.allowIdentifyShortcut || !stages.text?.result) return false;
  const similarStrength = automaticCandidateStrength(
    candidatesForAutomaticStep('similar', stages.similar?.result),
    config
  );
  if (
    similarStrength.confidence !== null &&
    similarStrength.confidence >= config.candidateThreshold &&
    usefulTextEvidence(stages.text.result)
  ) {
    return true;
  }
  const similarId = topCandidateId('similar', stages.similar?.result);
  const textId = topCandidateId('text', stages.text.result);
  return Boolean(similarId && textId && String(similarId) === String(textId));
};

export const automaticResultSummary = (
  step: AutoStepKey,
  result: any,
  config: AutoMatchConfig = AUTO_MATCH_CONFIG
) => {
  const strength = shouldAcceptCandidate(step, result, config);
  const hypothesis = result?.research?.hypotheses?.[0];
  const candidate = strength.candidate || hypothesis;
  const confidence =
    strength.confidence ?? automaticCandidateScore(hypothesis) ?? null;
  return {
    candidate,
    confidence,
    confident: strength.sufficient,
    label: candidate
      ? [candidate.brand, candidate.name || candidate.label]
          .filter(Boolean)
          .join(' ') || 'Candidate returned'
      : null,
  };
};
